import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { Fragment, type ComponentChildren, h, type VNode } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useTranslationContext } from "../context/translation.js";
import type { FormModel } from "../hooks/useForm.js";
import {
  isMarkdownContentType,
  isSafeMarkdownSize,
  SafeMarkdown,
} from "../components/SafeMarkdown.js";
import type {
  AcceptTosFormDesign,
  FormOfficerViewProps,
} from "./forms-types.js";

type ReaderDocument = {
  content: string;
  contentType: string;
};

type ReaderError = "load" | "format" | "large";

export type AcceptTosFormValues = {
  [TalerFormAttributes.DOWNLOADED_TERMS_OF_SERVICE]: boolean;
  [TalerFormAttributes.ACCEPTED_TERMS_OF_SERVICE]: string;
};

export function AcceptTosOfficerView({
  design,
  data,
}: FormOfficerViewProps): VNode {
  const { i18n } = useTranslationContext();
  const accepted = data[TalerFormAttributes.ACCEPTED_TERMS_OF_SERVICE];
  const acceptedVersion =
    typeof accepted === "string" && accepted.trim() ? accepted : undefined;
  const downloaded = data[TalerFormAttributes.DOWNLOADED_TERMS_OF_SERVICE];
  const providerName =
    design.type === "accept-tos" ? design.providerName?.trim() : undefined;
  // Translators: Status shown when no terms-acceptance value was stored.
  const notRecordedLabel = i18n.str`Not recorded`;
  // Translators: Heading for the organization that supplied the terms.
  const providerLabel = i18n.str`Provider`;

  return (
    <dl class="grid gap-4 sm:grid-cols-2">
      <div>
        <dt class="text-sm font-semibold text-gray-900 dark:text-gray-100">
          <i18n.Translate>Acceptance status</i18n.Translate>
        </dt>
        <dd class="mt-1 text-sm text-gray-700 dark:text-gray-300">
          {acceptedVersion ? i18n.str`Accepted` : notRecordedLabel}
        </dd>
      </div>
      <div>
        <dt class="text-sm font-semibold text-gray-900 dark:text-gray-100">
          <i18n.Translate>Accepted terms version</i18n.Translate>
        </dt>
        <dd class="mt-1 break-words font-mono text-sm text-gray-700 dark:text-gray-300">
          {acceptedVersion ?? notRecordedLabel}
        </dd>
      </div>
      <div>
        <dt class="text-sm font-semibold text-gray-900 dark:text-gray-100">
          <i18n.Translate>Terms opened or downloaded</i18n.Translate>
        </dt>
        <dd class="mt-1 text-sm text-gray-700 dark:text-gray-300">
          {downloaded === true
            ? i18n.str`Yes`
            : downloaded === false
              ? i18n.str`No`
              : notRecordedLabel}
        </dd>
      </div>
      {!providerName ? undefined : (
        <div>
          <dt class="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {providerLabel}
          </dt>
          <dd class="mt-1 text-sm text-gray-700 dark:text-gray-300">
            {providerName}
          </dd>
        </div>
      )}
    </dl>
  );
}

function mediaType(contentType: string | null): string {
  return contentType?.split(";", 1)[0].trim().toLowerCase() ?? "";
}

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  );
}

function safePdfFileName(providerName: string | undefined): string {
  const provider = providerName
    ?.normalize("NFKD")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${provider || "Terms-of-Service"}-Terms-of-Service.pdf`;
}

function TermsReaderDialog(props: {
  design: AcceptTosFormDesign;
  document: ReaderDocument | undefined;
  loading: boolean;
  error: ReaderError | undefined;
  onRetry: () => void;
  onClose: () => void;
}): VNode {
  const { onClose } = props;
  const { i18n } = useTranslationContext();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [downloadState, setDownloadState] = useState<
    "idle" | "loading" | "error"
  >("idle");
  // Translators: Button that retries loading the terms-of-service document.
  const retryLabel = i18n.str`Try again`;
  // Translators: Status shown while the terms-of-service PDF is downloading.
  const downloadingPdfLabel = i18n.str`Downloading PDF…`;
  // Translators: Button that downloads the terms-of-service document as a PDF.
  const downloadPdfLabel = i18n.str`Download PDF`;
  // Translators: Button that closes the terms reader. It does not accept the
  // terms.
  const doneLabel = i18n.str`Done`;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    previousFocus.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      const dialog = dialogRef.current;
      if (event.key !== "Tab" || !dialog) return;
      const focusable = focusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus.current?.focus();
    };
  }, [onClose]);

  async function downloadPdf(): Promise<void> {
    setDownloadState("loading");
    try {
      const response = await fetch(props.design.tosUrl, {
        headers: { Accept: "application/pdf" },
        cache: "no-cache",
      });
      if (
        !response.ok ||
        mediaType(response.headers.get("content-type")) !== "application/pdf"
      ) {
        throw Error("PDF response unavailable");
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      try {
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = safePdfFileName(props.design.providerName);
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
      setDownloadState("idle");
    } catch (error) {
      console.error("Could not download terms of service as PDF", error);
      setDownloadState("error");
    }
  }

  let errorMessage: ComponentChildren;
  switch (props.error) {
    case "format":
      errorMessage = i18n.str`The terms are not available as Markdown or plain text.`;
      break;
    case "large":
      // Translators: “terms” means the terms-of-service document, not words or
      // terminology.
      errorMessage = i18n.str`These terms are too large to display safely.`;
      break;
    case "load":
      errorMessage = i18n.str`The terms of service could not be loaded.`;
      break;
    default:
      errorMessage = undefined;
  }

  return (
    <div
      class="fixed inset-0 z-50 flex bg-black/50 p-0 sm:items-center sm:justify-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tos-reader-title"
        aria-describedby="tos-reader-description"
        tabIndex={-1}
        class="flex h-full w-full flex-col overflow-hidden bg-white shadow-xl sm:h-auto sm:max-h-[min(90vh,56rem)] sm:max-w-3xl sm:rounded-xl"
      >
        <div class="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-4 py-4 sm:px-6">
          <div class="min-w-0">
            <h2
              id="tos-reader-title"
              class="text-lg font-semibold text-gray-900"
            >
              <i18n.Translate>Terms of service</i18n.Translate>
            </h2>
            <p
              id="tos-reader-description"
              class="mt-1 truncate text-sm text-gray-500"
            >
              {props.design.providerName ?? props.design.tosUrl}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={i18n.str`Close terms of service`}
            class="grid h-11 w-11 shrink-0 place-items-center rounded-full text-2xl leading-none text-gray-600 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          {props.loading ? (
            <div class="grid min-h-48 place-items-center text-center">
              <p role="status" class="text-sm text-gray-600">
                <i18n.Translate>Loading terms of service…</i18n.Translate>
              </p>
            </div>
          ) : errorMessage ? (
            <div class="grid min-h-48 place-items-center text-center">
              <div>
                <p role="alert" class="text-sm font-medium text-red-700">
                  {errorMessage}
                </p>
                <button
                  type="button"
                  class="mt-4 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={props.onRetry}
                >
                  {retryLabel}
                </button>
              </div>
            </div>
          ) : props.document ? (
            isMarkdownContentType(props.document.contentType) ? (
              <SafeMarkdown
                content={props.document.content}
                tooLargeMessage={i18n.str`These terms are too large to display safely.`}
                onOpenLink={(url) =>
                  window.open(url, "_blank", "noopener,noreferrer")
                }
              />
            ) : (
              <pre class="whitespace-pre-wrap break-words font-sans text-sm leading-6 text-gray-800">
                {props.document.content}
              </pre>
            )
          ) : null}
        </div>

        <div class="shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex sm:items-center sm:justify-between sm:gap-3 sm:px-6">
          <div>
            {!props.design.linkOnly && (
              <button
                type="button"
                disabled={downloadState === "loading"}
                class="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 disabled:cursor-wait disabled:opacity-50 sm:w-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => void downloadPdf()}
              >
                {downloadState === "loading"
                  ? downloadingPdfLabel
                  : downloadPdfLabel}
              </button>
            )}
            {downloadState === "error" && (
              <p role="alert" class="mt-2 text-sm text-red-700">
                <i18n.Translate>
                  The PDF could not be downloaded.
                </i18n.Translate>
              </p>
            )}
          </div>
          <button
            type="button"
            class="mt-3 w-full rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:mt-0 sm:w-auto"
            onClick={onClose}
          >
            {doneLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AcceptTosForm(props: {
  name: string;
  design: AcceptTosFormDesign;
  model: FormModel<AcceptTosFormValues>;
  disabled?: boolean;
  focus?: boolean;
  onSubmit?: () => void;
}): VNode {
  const { i18n } = useTranslationContext();
  const downloaded = props.model.getUiFieldForAttribute(
    TalerFormAttributes.DOWNLOADED_TERMS_OF_SERVICE,
  );
  const accepted = props.model.getUiFieldForAttribute(
    TalerFormAttributes.ACCEPTED_TERMS_OF_SERVICE,
  );
  const [open, setOpen] = useState(false);
  const [document, setDocument] = useState<ReaderDocument>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReaderError>();
  const request = useRef<AbortController>();
  const readButton = useRef<HTMLButtonElement>(null);

  useEffect(
    () => () => {
      request.current?.abort();
    },
    [],
  );

  async function loadTerms(): Promise<void> {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setOpen(true);
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetch(props.design.tosUrl, {
        headers: { Accept: "text/markdown, text/plain;q=0.9" },
        cache: "no-cache",
        signal: controller.signal,
      });
      if (!response.ok) throw Error(`HTTP ${response.status}`);
      const type = mediaType(response.headers.get("content-type"));
      if (type !== "text/markdown" && type !== "text/plain") {
        setError("format");
        return;
      }
      const content = await response.text();
      if (!isSafeMarkdownSize(content)) {
        setError("large");
        return;
      }
      setDocument({ content, contentType: type });
      downloaded.field.onChange(true);
    } catch (cause) {
      if (controller.signal.aborted) return;
      console.error("Could not load terms of service", cause);
      setError("load");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  function openReader(): void {
    if (document) {
      setOpen(true);
      return;
    }
    void loadTerms();
  }

  function closeReader(): void {
    request.current?.abort();
    setLoading(false);
    setOpen(false);
  }

  const checkboxId = `${props.name}-accepted-terms-of-service`;
  const hintId = `${props.name}-accepted-terms-of-service-hint`;
  const hasRead = downloaded.field.value === true;
  const isAccepted = accepted.field.value === props.design.tosVersion;
  // Translators: %1$s is the name of the organization or service provider
  // whose terms the user must review.
  const providerDescription = i18n.str`Read the terms provided by ${
    props.design.providerName ?? ""
  } before recording your acceptance.`;
  // Translators: Button that reopens a terms-of-service document the user has
  // already viewed.
  const readAgainLabel = i18n.str`Read terms again`;
  // Translators: Button that opens the terms-of-service document in an in-app
  // reader.
  const readLabel = i18n.str`Read terms`;
  // Translators: The accepted version is the exact version of the
  // terms-of-service document that was opened in the reader.
  const reviewedVersionHint = i18n.str`Your acceptance applies to the version shown above.`;

  return (
    <Fragment>
      <form
        name={props.name}
        class="max-w-2xl rounded-lg bg-white p-5 shadow-sm ring-1 ring-gray-900/5 sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          props.onSubmit?.();
        }}
      >
        <div class="max-w-2xl">
          <h2 class="text-lg font-semibold text-gray-900">
            <i18n.Translate>Review the terms of service</i18n.Translate>
          </h2>
          <p class="mt-2 text-sm leading-6 text-gray-600">
            {props.design.providerName
              ? providerDescription
              : i18n.str`Read the terms before recording your acceptance.`}
          </p>

          <button
            ref={readButton}
            type="button"
            disabled={props.disabled}
            class="mt-5 inline-flex min-h-11 items-center justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-default disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={openReader}
            autofocus={props.focus}
          >
            {hasRead ? readAgainLabel : readLabel}
          </button>

          <div class="mt-6 border-t border-gray-200 pt-5">
            <label
              for={checkboxId}
              class={`flex items-start gap-3 rounded-md p-3 -m-3 ${
                hasRead && !props.disabled
                  ? "cursor-pointer hover:bg-gray-50"
                  : "cursor-not-allowed"
              }`}
            >
              <span class="sr-only">
                {i18n.str`Accept the terms of service`}
              </span>
              <input
                id={checkboxId}
                type="checkbox"
                class="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:opacity-50"
                checked={isAccepted}
                disabled={!hasRead || props.disabled}
                aria-describedby={hintId}
                onChange={(event) => {
                  accepted.field.onChange(
                    event.currentTarget.checked
                      ? props.design.tosVersion
                      : undefined,
                  );
                }}
              />
              <span>
                <span class="block text-sm font-medium leading-6 text-gray-900">
                  <i18n.Translate>
                    I have read and accept the terms of service
                  </i18n.Translate>
                </span>
                <span
                  id={hintId}
                  class="mt-0.5 block text-sm leading-5 text-gray-500"
                >
                  {hasRead
                    ? reviewedVersionHint
                    : i18n.str`Open the terms before accepting them.`}
                </span>
              </span>
            </label>
          </div>
        </div>
      </form>
      {open && (
        <TermsReaderDialog
          design={props.design}
          document={document}
          loading={loading}
          error={error}
          onRetry={() => void loadTerms()}
          onClose={closeReader}
        />
      )}
    </Fragment>
  );
}
